import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React from 'react';
import Text from '../../../components/common/custom-text/custom-text.jsx';
import { formatCurrency } from '../../../utils/currency-utils.js';
import styles from './order-summary.module.scss';

function removeOrAdjustFreeWorkspaceTier(priceList, plan) {
  if (priceList.length === 0) {
    return [];
  }

  if (plan.value === 'FREELANCER') {
    const [first, ...rest] = priceList;
    if (first.count > 1) {
      return [{ price: first.price, count: first.count - 1 }, ...rest];
    }
    return rest;
  }

  return priceList.filter(tier => tier.price !== 0);
}

function generateLineItems(priceList, plan, planPrice, isAnnual) {
  const lineItems = [];
  const isFreelancer = plan.value === 'FREELANCER';

  const basePrice = isFreelancer ? (priceList[0]?.price || 0) : planPrice;

  lineItems.push({
    id: 'li_plan',
    description: `${plan.label} Plan`,
    price: basePrice,
    subDescription: isAnnual && 'Includes 2 free months',
  });

  lineItems.push({
    id: 'li_free_workspaces',
    description:
      plan.value === 'FREELANCER'
        ? 'First Workspace'
        : `First 5 Workspaces`,
    price: 0,
    subDescription: 'Included in plan',
  });

  const includedCount = plan.value === 'FREELANCER' ? 1 : 5;
  let cursor = includedCount;

  const paidWorkspacePricing = removeOrAdjustFreeWorkspaceTier(priceList, plan);
  
  paidWorkspacePricing.forEach(({ price, count }) => {
    const start = cursor + 1;
    const end = cursor + count;
    const description =
      count === 1
        ? `Workspace ${start}`
        : `Workspaces ${start} - ${end}`;
    const unitDesc = `${formatCurrency(price)} / Workspace`;
    lineItems.push({
      id: `li_workspace_${price}`,
      description: description,
      price: price * count,
      subDescription: unitDesc,
    });
    cursor = end;
  });

  return lineItems;
}

function OrderSummary({ quote, formData, loading }) {
  const { total, daysUntilDue, discount, planPrice, pricePerUnit = {}, coupon } = quote || {};
  const { billingInterval, plan } = formData || {};

  const isAnnual = billingInterval === 'annual';

  const sortedPricePerUnitArray = Object.entries(pricePerUnit)
    .map(([priceStr, count]) => ({ price: Number(priceStr), count }))
    .sort((a, b) => b.price - a.price);
  const lineItems = generateLineItems(sortedPricePerUnitArray, plan, planPrice, isAnnual);

  return (
    <div className={styles.orderSummaryCard} data-testid="order-summary">
      <div className={styles.orderDetails}>
        <Text as="h5" variant="h5" color="#324975" className={styles.header}>
          Order Summary
          {loading && (
            <FontAwesomeIcon
              icon={faCircleNotch}
              spin
              size="sm"
              aria-label="Loading order summary"
            />
          )}
        </Text>
        <ul className={styles.orderDetailsList}>
          {lineItems.map(item => (
            <li key={item.id} className={styles.orderListItem}>
              <div className={styles.orderListMain}>
                <Text variant="b1" color="#324975">
                  {item.description}
                </Text>
                <Text variant="b1" color="#324975">
                  {formatCurrency(item.price)}
                </Text>
              </div>
              {item.subDescription && (
                <Text variant="b2" color="#6B7280" className={styles.subDescription}>
                  {"(" + item.subDescription + ")"}
                </Text>
              )}
            </li>
          ))}
        </ul>
      </div>
      <hr className={styles.divider} />
      <div className={styles.orderDiscounts}>
        <ul className={styles.orderDiscountsList}>
          {coupon?.valid && (
            <li className={styles.orderListItem}>
              <div className={styles.orderListMain}>  
                <Text variant="b2" color="#324975">
                  {coupon.percentOff
                    ? `${coupon.percentOff}% coupon applied`
                    : coupon.amountOff
                    ? `${formatCurrency(coupon.amountOff)} coupon applied`
                    : 'Coupon applied'}
                </Text>
                <Text variant="b2" color="#324975">
                  -{formatCurrency(discount || coupon.amountOff || 0)}
                </Text>
              </div>
            </li>
          )}
        </ul>
      </div>
      <div className={styles.orderDiscountsTotal}>
        <Text variant="b1" color="#324975">
          Total due on {moment().add(daysUntilDue, 'days').format('ll')}
        </Text>
        <Text variant="b1" color="#324975">
          {formatCurrency(total)}
        </Text>
      </div>
      <Text as="i" variant="b2" color="#6B7280">
        You will be reminded 2 days before we charge you.
      </Text>
      <hr className={styles.divider} />
      <div className={styles.totalDue}>
        <Text variant="bbL" color="#324975">
          Total Due Today
        </Text>
        <span className={styles.totalDueAmount}>
          <Text variant="bb1" color="#324975">
            {formatCurrency(0)}
          </Text>
        </span>
      </div>
    </div>
  );
}

export default OrderSummary;